import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const IncomeTax = () => {
  const [userType, setUserType] = useState("individual");
  const [taxableIncome, setTaxableIncome] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [deductions, setDeductions] = useState(0);
  const [taxCredits, setTaxCredits] = useState(0);
  const [numEmployees, setNumEmployees] = useState(1);
  const [totalTaxPayable, setTotalTaxPayable] = useState(0);
  const [taxPerPerson, setTaxPerPerson] = useState(0);

  useEffect(() => {
    calculateIncomeTax();

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [taxableIncome, taxRate, deductions, taxCredits, numEmployees]);

  const toggleUserType = (type) => {
    setUserType(type);
  };

  const calculateIncomeTax = () => {
    // Ensure that if fields are empty or invalid, we use 0
    const taxBeforeCredits =
      ((parseFloat(taxableIncome) || 0) * (parseFloat(taxRate) || 0)) / 100 -
      (parseFloat(deductions) || 0);
    const totalTax = taxBeforeCredits - (parseFloat(taxCredits) || 0);

    // Calculate tax per person if it's a business
    const taxPerPersonResult =
      userType === "business" && numEmployees > 1
        ? totalTax / numEmployees
        : totalTax;

    setTotalTaxPayable(totalTax);
    setTaxPerPerson(taxPerPersonResult);
  };

  return (
    <Layout title="Income Tax Calculator" path="income-tax">
      <section
        className="topics-detail-section section-padding"
        id="topics-detail"
      >
        <div className="container">
          <div className="row">
            {/* Income Tax Calculator Section */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Income Tax Calculator</h3>

                <div className="container">
                  {/* Select Type Radio Buttons (Individual or Business) */}
                  <div className="row justify-content-center mb-4">
                    <div className="col-md-6 col-lg-4 text-center">
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="userType"
                          id="individual"
                          value="individual"
                          checked={userType === "individual"}
                          onChange={() => toggleUserType("individual")}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="individual"
                        >
                          Individual
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="userType"
                          id="business"
                          value="business"
                          checked={userType === "business"}
                          onChange={() => toggleUserType("business")}
                        />
                        <label className="form-check-label" htmlFor="business">
                          Business
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Input Fields for Tax Calculation */}
                  <div className="row gy-3">
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="taxableIncome" className="form-label">
                        Taxable Income (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Taxable Income:</strong> The total income subject to taxation after deductions and exemptions."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="taxableIncome"
                        step="0.01"
                        placeholder="Enter taxable income"
                        value={taxableIncome}
                        onChange={(e) =>
                          setTaxableIncome(parseFloat(e.target.value))
                        }
                      />
                      {/* <small className="form-text text-muted">in ₹</small> */}
                    </div>

                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="taxRate" className="form-label">
                        Tax Rate (%)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Tax Rate:</strong> The percentage of your taxable income that is paid as tax."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="taxRate"
                        step="0.01"
                        placeholder="Enter tax rate"
                        value={taxRate}
                        onChange={(e) => setTaxRate(parseFloat(e.target.value))}
                      />
                      {/* <small className="form-text text-muted">
                        in percentage
                      </small> */}
                    </div>

                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="deductions" className="form-label">
                        Deductions (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Deductions:</strong> Amounts you can subtract from your income to lower your taxable income."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="deductions"
                        step="0.01"
                        placeholder="Enter deductions"
                        value={deductions}
                        onChange={(e) =>
                          setDeductions(parseFloat(e.target.value))
                        }
                      />
                      {/* <small className="form-text text-muted">in ₹</small> */}
                    </div>

                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="taxCredits" className="form-label">
                        Tax Credits (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Tax Credits:</strong> Reductions in the amount of tax owed, directly reducing the final tax payable."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="taxCredits"
                        step="0.01"
                        placeholder="Enter tax credits"
                        value={taxCredits}
                        onChange={(e) =>
                          setTaxCredits(parseFloat(e.target.value))
                        }
                      />
                      {/* <small className="form-text text-muted">in ₹</small> */}
                    </div>

                    {/* Additional Fields for Business (Optional) */}
                    {userType === "business" && (
                      <div className="col-md-6 col-lg-4">
                        <label htmlFor="numEmployees" className="form-label">
                          Number of Employees
                          <span
                            className="ms-2 text-muted"
                            data-bs-toggle="popover"
                            data-bs-html="true"
                            data-bs-placement="top"
                            data-bs-content="<strong>Number of Employees:</strong> The total number of employees working in the business or organization."
                            style={{ cursor: "pointer" }}
                          >
                            <i className="bi bi-info-circle"></i>
                          </span>
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="numEmployees"
                          min="1"
                          placeholder="Enter number of employees"
                          value={numEmployees}
                          onChange={(e) =>
                            setNumEmployees(parseInt(e.target.value))
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Total Tax Payable (₹):{" "}
                        <span id="totalTaxPayable" className="fw-bold ms-3">
                          {totalTaxPayable.toFixed(2)}
                        </span>
                      </h6>
                      <h6>
                        Tax Payable per Person (₹):{" "}
                        <span id="taxPerPerson" className="fw-bold ms-3">
                          {taxPerPerson.toFixed(2)}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Income Tax Calculator
                </h3>

                {/* What is an Income Tax Calculator? */}
                <h4 className="mt-3 mb-3">What is an Income Tax Calculator?</h4>
                <p>
                  An <strong>Income Tax Calculator</strong> is a tool designed
                  to help individuals and businesses accurately compute the
                  amount of income tax owed based on their taxable income,
                  applicable tax rates, and eligible deductions. It simplifies
                  complex tax calculations and ensures users are well-prepared
                  for tax obligations.
                </p>

                {/* Why Use an Income Tax Calculator? */}
                <h4 className="mt-4 mb-3">Why Use an Income Tax Calculator?</h4>
                <p>Using an income tax calculator offers several advantages:</p>
                <ul>
                  <li>
                    <strong>Accuracy:</strong> Minimizes errors and provides a
                    precise estimate of the tax liability.
                  </li>
                  <li>
                    <strong>Time-Saving:</strong> Automates complex tax
                    calculations that would otherwise take hours to complete
                    manually.
                  </li>
                  <li>
                    <strong>Financial Planning:</strong> Helps plan and allocate
                    finances effectively by knowing expected tax dues in
                    advance.
                  </li>
                  <li>
                    <strong>Compliance:</strong> Ensures that users stay
                    compliant with tax regulations by considering allowable
                    deductions and credits.
                  </li>
                </ul>

                {/* How to Use an Income Tax Calculator? */}
                <h4 className="mt-4 mb-3">
                  How to Use an Income Tax Calculator?
                </h4>
                <p>
                  To make the most of an income tax calculator, follow these
                  steps:
                </p>
                <ul>
                  <li>
                    Enter your <strong>taxable income</strong> for the relevant
                    period.
                  </li>
                  <li>
                    Input the <strong>tax rate</strong> that applies to your
                    income bracket.
                  </li>
                  <li>
                    Provide details about <strong>deductions</strong> and{" "}
                    <strong>tax credits</strong> to be applied.
                  </li>
                  <li>
                    The calculator will then compute the total tax due and, for
                    businesses, provide tax liability per employee if needed.
                  </li>
                </ul>
                <p>
                  This allows users to get a clear view of their tax liabilities
                  with minimal effort.
                </p>

                {/* What Factors Affect Income Tax Calculation? */}
                <h4 className="mt-4 mb-3">
                  What Factors Affect Income Tax Calculation?
                </h4>
                <p>
                  Several factors can influence the accuracy and output of
                  income tax calculations:
                </p>
                <ul>
                  <li>
                    <strong>Taxable Income:</strong> The income on which tax is
                    calculated after subtracting applicable exemptions.
                  </li>
                  <li>
                    <strong>Tax Rate:</strong> The rate at which your income is
                    taxed, which may vary based on income brackets and regions.
                  </li>
                  <li>
                    <strong>Deductions:</strong> Amounts subtracted from your
                    total income for eligible expenses (e.g., mortgage interest,
                    education expenses).
                  </li>
                  <li>
                    <strong>Tax Credits:</strong> Direct reductions to the
                    amount of tax owed, providing significant relief.
                  </li>
                  <li>
                    <strong>Filing Status:</strong> For individuals, filing as
                    single, married, or head of household can change the tax
                    rate.
                  </li>
                </ul>

                {/* New Additional Questions */}
                <h4 className="mt-4 mb-3">
                  Why Do Tax Rates Differ Between Individuals and Businesses?
                </h4>
                <p>
                  The tax rates for individuals and businesses often differ due
                  to the nature of their income. Individuals are typically taxed
                  based on personal income tax brackets, whereas businesses are
                  taxed on profits, with varying rates depending on their legal
                  structure (e.g., sole proprietorship, corporation).
                </p>

                <h4 className="mt-4 mb-3">
                  What Are the Tax Benefits for Businesses?
                </h4>
                <p>
                  Businesses may benefit from various tax deductions such as
                  employee salaries, operating expenses, capital expenditures,
                  and other business-related costs. These deductions can reduce
                  the taxable income of a business, leading to a lower overall
                  tax liability.
                </p>

                <h4 className="mt-4 mb-3">
                  What Is the Difference Between Tax Deductions and Tax Credits?
                </h4>
                <p>
                  <strong>Tax deductions</strong> reduce the taxable income,
                  whereas <strong>tax credits</strong> reduce the actual tax
                  liability. In simple terms, tax deductions lower the income on
                  which tax is calculated, and tax credits directly reduce the
                  amount of tax owed.
                </p>

                {/* What Are the Benefits of Using an Income Tax Calculator? */}
                <h4 className="mt-4 mb-3">
                  What Are the Benefits of Using an Income Tax Calculator?
                </h4>
                <p>
                  Leveraging an income tax calculator comes with several
                  benefits:
                </p>
                <ul>
                  <li>
                    <strong>Financial Preparedness:</strong> Helps users
                    understand and prepare for tax dues well in advance.
                  </li>
                  <li>
                    <strong>Enhanced Budgeting:</strong> Assists in setting
                    aside the right amount for tax payments, aiding in better
                    cash flow management.
                  </li>
                  <li>
                    <strong>Quick Adjustments:</strong> Allows users to input
                    different scenarios to understand how changes in income or
                    deductions affect tax liability.
                  </li>
                  <li>
                    <strong>Stress-Free Tax Season:</strong> Reduces the stress
                    associated with manual calculations and potential errors
                    during filing.
                  </li>
                </ul>

                <p>
                  In conclusion, the <strong>Income Tax Calculator</strong> is
                  an essential tool for individuals and businesses aiming for
                  accuracy in tax calculation and better financial planning. By
                  using this tool, users can navigate the complexities of tax
                  preparation with confidence and ease.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default IncomeTax;
